import React, { useEffect, useState, useRef, useCallback } from "react";
import { EventNodeData, HandleConfig } from "../../types";
import { Position } from "reactflow";
import ConfigBoxToolbar from "../../components/shared/ConfigBoxToolbar";
import LabelWithVisibilityToggle, { VisibilityOption } from "../../components/shared/LabelWithVisibilityToggle";
import AutoComplete from "../../components/shared/AutoComplete";
import HandleManager from "../../components/shared/HandleManager";
// Import the shared suggestions
import { CUSTOM_LABEL_SUGGESTIONS } from "../../data/suggestions"; // Add CUSTOM_LABEL_SUGGESTIONS

interface EventConfigBoxProps {
  nodeId: string;
  data: EventNodeData;
  onUpdate?: (data: Partial<EventNodeData>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onDetachFromGroup?: () => void; // Add prop
  isAdvancedMode?: boolean;
  portalRoot?: HTMLElement | null;
}

// Keep EVENT_SUGGESTIONS as it's specific to this component
const EVENT_SUGGESTIONS = [
  { value: "page_view", label: "Page View" },
  { value: "session_start", label: "Session Start" },
  { value: "user_signup", label: "User Signup" },
  { value: "user_login", label: "User Login" },
  { value: "add_to_cart", label: "Add to Cart" },
  { value: "remove_from_cart", label: "Remove from Cart" },
  { value: "begin_checkout", label: "Begin Checkout" },
  { value: "purchase", label: "Purchase" },
  { value: "refund", label: "Refund" },
  { value: "product_view", label: "Product View" },
  { value: "product_list_view", label: "Product List View" },
  { value: "product_click", label: "Product Click" },
  { value: "promotion_view", label: "Promotion View" },
  { value: "promotion_click", label: "Promotion Click" },
  { value: "search", label: "Search" },
  { value: "search_no_results", label: "Search - No Results" },
  { value: "filter_applied", label: "Filter Applied" },
  { value: "sort_applied", label: "Sort Applied" },
  { value: "wishlist_add", label: "Wishlist Add" },
  { value: "wishlist_remove", label: "Wishlist Remove" },
  { value: "subscription_start", label: "Subscription Start" },
  { value: "subscription_cancel", label: "Subscription Cancel" },
  { value: "subscription_renew", label: "Subscription Renew" },
  { value: "newsletter_signup", label: "Newsletter Signup" },
  { value: "form_submission", label: "Form Submission" },
  { value: "contact_request", label: "Contact Request" },
  { value: "click", label: "Click" },
  { value: "hover", label: "Hover" },
  { value: "video_play", label: "Video Play" },
  { value: "video_pause", label: "Video Pause" },
  { value: "video_complete", label: "Video Complete" },
  { value: "social_share", label: "Social Share" },
  { value: "error", label: "Error" },
  { value: "404_error", label: "404 Error" },
  { value: "logout", label: "Logout" },
];

export default function EventConfigBox({
  nodeId,
  data,
  onUpdate,
  onDelete,
  onDuplicate,
  onDetachFromGroup, // Destructure prop
  portalRoot,
  isAdvancedMode = false,
}: EventConfigBoxProps) {
  // Initialize state with props
  const [name, setName] = useState(data.name || "");
  const [label, setLabel] = useState(data.label || "");
  const [notes, setNotes] = useState(data.notes || "");

  const [nameVisibility, setNameVisibility] = useState<VisibilityOption>(data.nameVisibility ?? "visible");
  const [labelVisibility, setLabelVisibility] = useState<VisibilityOption>(data.labelVisibility ?? "visible");
  const [notesVisibility, setNotesVisibility] = useState<VisibilityOption>(data.notesVisibility ?? "visible");

  const defaultHandles: HandleConfig[] = [
    { id: 'default-source', type: 'source', position: Position.Right, color: '#3b82f6' },
  ];

  const [handles, setHandles] = useState<HandleConfig[]>(
    Array.isArray(data.handles) ? [...data.handles] : defaultHandles
  );

  const isFirstRender = useRef(true);
  const prevUpdateRef = useRef(""); // Last update sent to parent
  const prevPropsRef = useRef("");  // Last props received from parent

  useEffect(() => {
    const incomingProps = JSON.stringify({
      name: data.name || "",
      label: data.label || "",
      notes: data.notes || "",
      nameVisibility: data.nameVisibility ?? "visible",
      labelVisibility: data.labelVisibility ?? "visible",
      notesVisibility: data.notesVisibility ?? "visible",
      handles: Array.isArray(data.handles) ? data.handles : defaultHandles,
    });

    if (incomingProps !== prevPropsRef.current) {
      prevPropsRef.current = incomingProps;

      setName(data.name || "");
      setLabel(data.label || "");
      setNotes(data.notes || "");
      setNameVisibility(data.nameVisibility ?? "visible");
      setLabelVisibility(data.labelVisibility ?? "visible");
      setNotesVisibility(data.notesVisibility ?? "visible");

      if (Array.isArray(data.handles)) {
        setHandles([...data.handles]);
      } else {
        setHandles(defaultHandles);
      }
    }

    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [data]);

  useEffect(() => {
    if (isFirstRender.current) return;

    const outgoingUpdate = JSON.stringify({
      name,
      label,
      notes,
      nameVisibility,
      labelVisibility,
      notesVisibility,
      handles,
    });

    if (outgoingUpdate !== prevUpdateRef.current && onUpdate) {
      prevUpdateRef.current = outgoingUpdate;
      onUpdate({
        name,
        label,
        notes,
        nameVisibility,
        labelVisibility,
        notesVisibility,
        handles,
      });
    }
  }, [
    name,
    label,
    notes,
    nameVisibility,
    labelVisibility,
    notesVisibility,
    handles,
    onUpdate,
  ]);

  const handleHandlesChange = useCallback((newHandles: HandleConfig[]) => {
    setHandles(newHandles);
  }, []);

  const toggleVisibility = (
    current: VisibilityOption,
    setter: React.Dispatch<React.SetStateAction<VisibilityOption>>
  ) => {
    const nextVisibility = current === "hidden" ? "visible" : "hidden";
    setter(nextVisibility);
  };

  return (
    <div className="space-y-3 relative">
      {/* Add the toolbar */}
      <ConfigBoxToolbar
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onDetachFromGroup={onDetachFromGroup} // Pass prop down
        isAdvancedMode={isAdvancedMode}
      />

      <div className="pt-1">
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 mt-3">
          Event Name
        </label>
        <AutoComplete
          value={name}
          onChange={(value: string) => {
            setName(value);
          }}
          onSelect={(option) => {
            if (typeof option === 'object') {
              setName(option.label);
              // Optionally update label based on selection, or keep it separate
              // setLabel(option.value); // Uncomment if label should auto-populate from event value
            } else {
              setName(option);
              // Optionally update label based on selection, or keep it separate
              // setLabel(option); // Uncomment if label should auto-populate from event value
            }
          }}
          options={EVENT_SUGGESTIONS}
          placeholder="Select or enter event name"
          dropdownClassName="absolute z-[100]"
          portalContainer={portalRoot}
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Custom Label" // Changed label text
          visibility={labelVisibility}
          onToggleVisibility={() => toggleVisibility(labelVisibility, setLabelVisibility)}
          className="mb-1"
        />
        {/* Replace input with AutoComplete */}
        <AutoComplete
          value={label} // Use 'label' state here
          onChange={setLabel} // Update 'label' state
          options={CUSTOM_LABEL_SUGGESTIONS} // Use shared custom label suggestions
          placeholder="Set a Custom Label (optional)" // Updated placeholder
          dropdownClassName="absolute z-[99]" // Adjust z-index if needed
          portalContainer={portalRoot}
        />
      </div>

      <div>
        <LabelWithVisibilityToggle
          label="Notes"
          visibility={notesVisibility}
          onToggleVisibility={() => toggleVisibility(notesVisibility, setNotesVisibility)}
          className="mb-1"
        />
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes about this event (optional)"
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out resize-y min-h-[80px] max-h-[400px]"
          maxLength={500} // Limit to 500 characters
        />
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
          {notes.length}/500
        </div>
      </div>
      {/* --- Handle Manager --- */}
      <div>
        <HandleManager
          nodeId={nodeId}
          handles={handles}
          onChange={handleHandlesChange}
          maxHandles={6}
          portalRoot={portalRoot}
        />
      </div>
    </div>
  );
}
